// FinanceTeamFees — team fee ledger (the TeamFees + Additional Ball
// + Additional Player + Practice Balls + Fine tabs from the workbook,
// all rolled into a single category-tabbed ledger).
//
// Layout:
//   • Category strip — pills for each category with total collected
//     shown inline. Selecting one filters the ledger below.
//   • Ledger — table of payments in the selected category.
//   • Add Payment modal.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus, X, Pencil, Trash2, Receipt, Trophy, Users
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import {
  fmtMoney, fmtDate, todayIso,
  TEAM_FEE_CATEGORIES, CATEGORY_LABEL, CATEGORY_TONES, PAYMENT_MODES
} from "./shared";

const EMPTY = {
  registeredTeamId: "",
  category: "Registration",
  amount: "",
  paidDate: "",
  paymentMode: "Zelle",
  notes: ""
};

export default function FinanceTeamFees({ seasonId, canWrite }) {
  const toast = useToast();
  const [payments,   setPayments]   = useState([]);
  const [teams,      setTeams]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [category,   setCategory]   = useState("Registration");
  const [busy,       setBusy]       = useState(false);
  const [showForm,   setShowForm]   = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [form,       setForm]       = useState(() => ({ ...EMPTY, paidDate: todayIso() }));

  async function refresh() {
    if (!seasonId) return;
    setLoading(true);
    try {
      const [pl, tl] = await Promise.all([
        api.listTeamFees(seasonId),
        api.listRegisteredTeams()
      ]);
      setPayments(pl);
      setTeams(tl);
    } catch (err) {
      toast.error(err.message || "Couldn't load team fees.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [seasonId]);

  useEffect(() => {
    if (!showForm) return;
    const onKey = e => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showForm]);

  const totalsByCategory = useMemo(() => {
    const map = new Map();
    for (const c of TEAM_FEE_CATEGORIES) map.set(c.code, { total: 0, count: 0 });
    for (const p of payments) {
      const bucket = map.get(p.category) || { total: 0, count: 0 };
      bucket.total += Number(p.amount || 0);
      bucket.count += 1;
      map.set(p.category, bucket);
    }
    return map;
  }, [payments]);

  const rows = useMemo(
    () => payments.filter(p => p.category === category),
    [payments, category]
  );

  // Which teams are still unpaid for the currently-selected category?
  // Applies only to Registration and BallPayment — the others are ad-hoc.
  const unpaidTeams = useMemo(() => {
    if (category !== "Registration" && category !== "BallPayment") return [];
    const paid = new Set(rows.map(p => p.registeredTeamId));
    return teams
      .filter(t => t.status !== "Rejected" && !paid.has(t.id))
      .sort((a, b) => (a.teamName || "").localeCompare(b.teamName || ""));
  }, [teams, rows, category]);

  function openCreate() {
    setEditingId(null);
    setForm({ ...EMPTY, category, paidDate: todayIso() });
    setShowForm(true);
  }
  function openEdit(p) {
    setEditingId(p.id);
    setForm({
      registeredTeamId: String(p.registeredTeamId || ""),
      category:         p.category,
      amount:           String(p.amount ?? ""),
      paidDate:         p.paidDate || todayIso(),
      paymentMode:      p.paymentMode || "",
      notes:            p.notes || ""
    });
    setShowForm(true);
  }
  function close() { setShowForm(false); setEditingId(null); }

  async function submit(e) {
    e.preventDefault();
    if (!form.registeredTeamId) { toast.error("Team required."); return; }
    if (!form.amount)           { toast.error("Amount required."); return; }
    if (!form.paidDate)         { toast.error("Paid date required."); return; }
    setBusy(true);
    try {
      const payload = {
        seasonId,
        registeredTeamId: Number(form.registeredTeamId),
        category:         form.category,
        amount:           Number(form.amount),
        paidDate:         form.paidDate,
        paymentMode:      form.paymentMode || null,
        notes:            form.notes || null
      };
      if (editingId) {
        await api.updateTeamFee(editingId, payload);
        toast.success("Payment updated.");
      } else {
        await api.createTeamFee(payload);
        toast.success("Payment recorded.");
      }
      close();
      await refresh();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setBusy(false); }
  }

  async function remove(p) {
    if (!confirm(`Delete this ${CATEGORY_LABEL(p.category)} payment (${fmtMoney(p.amount)})?`)) return;
    try {
      await api.deleteTeamFee(p.id);
      toast.success("Deleted.");
      await refresh();
    } catch (err) { toast.error(err.message || "Delete failed."); }
  }

  const activeCatMeta = TEAM_FEE_CATEGORIES.find(c => c.code === category) || TEAM_FEE_CATEGORIES[0];

  return (
    <div className="stack" style={{gap:16}}>
      {/* Category strip */}
      <div className="fin-catStrip" role="tablist" aria-label="Fee categories">
        {TEAM_FEE_CATEGORIES.map(c => {
          const totals = totalsByCategory.get(c.code) || { total: 0, count: 0 };
          const active = category === c.code;
          return (
            <button
              key={c.code}
              type="button"
              role="tab"
              aria-selected={active}
              className={`fin-catCard fin-catCard--${c.tone}${active ? " fin-catCard--active" : ""}`}
              onClick={() => setCategory(c.code)}
              style={{ "--cat-color": CATEGORY_TONES[c.tone] }}
            >
              <span className="fin-catCard-eyebrow">{c.label}</span>
              <b className="fin-catCard-total">{fmtMoney(totals.total)}</b>
              <small>{totals.count} entries</small>
            </button>
          );
        })}
      </div>

      <Panel title={
        <div style={{display:"flex", alignItems:"center", gap:10, flex:1, flexWrap:"wrap"}}>
          <Receipt size={18}/>
          <span>{activeCatMeta.label} · Ledger</span>
          {canWrite && (
            <button type="button" className="btn" onClick={openCreate} style={{marginLeft:"auto"}}>
              <Plus size={14}/> Record Payment
            </button>
          )}
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading…</p>}
        {!loading && rows.length === 0 && (
          <div className="league-annEmpty">
            <Receipt size={28}/>
            <p>No {activeCatMeta.label.toLowerCase()} payments recorded yet.</p>
          </div>
        )}

        {rows.length > 0 && (
          <div className="fin-tableWrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th style={{textAlign:"right"}}>Amount</th>
                  <th>Date</th>
                  <th>Mode</th>
                  <th>Notes</th>
                  {canWrite && <th className="fin-table-actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.id}>
                    <td><b>{p.teamName || "—"}</b></td>
                    <td className="fin-table-money">{fmtMoney(p.amount)}</td>
                    <td>{fmtDate(p.paidDate)}</td>
                    <td>
                      {p.paymentMode
                        ? <span className="fin-pill">{p.paymentMode}</span>
                        : "—"}
                    </td>
                    <td className="fin-table-notes">{p.notes || ""}</td>
                    {canWrite && (
                      <td className="fin-table-actions">
                        <button type="button" className="league-annCard-iconBtn"
                          onClick={() => openEdit(p)} title="Edit">
                          <Pencil size={12}/>
                        </button>
                        <button type="button" className="league-annCard-iconBtn league-annCard-iconBtn--danger"
                          onClick={() => remove(p)} title="Delete">
                          <Trash2 size={12}/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="fin-table-total">
                  <td><b>Total</b></td>
                  <td className="fin-table-money"><b>{fmtMoney(rows.reduce((s, r) => s + Number(r.amount || 0), 0))}</b></td>
                  <td colSpan={canWrite ? 4 : 3}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {unpaidTeams.length > 0 && (
        <Panel title={
          <div style={{display:"flex", alignItems:"center", gap:8, flex:1}}>
            <Users size={16}/>
            <span>Awaiting {activeCatMeta.label}</span>
            <em style={{marginLeft:"auto", color:"var(--muted)", fontStyle:"normal", fontSize:12}}>
              {unpaidTeams.length} team{unpaidTeams.length === 1 ? "" : "s"}
            </em>
          </div>
        }>
          <div className="fin-unpaidGrid">
            {unpaidTeams.map(t => (
              <div key={t.id} className="fin-unpaidChip">
                <Trophy size={11}/>
                <span>{t.teamName}</span>
                {canWrite && (
                  <button
                    type="button"
                    className="fin-unpaidChip-btn"
                    onClick={() => {
                      setEditingId(null);
                      setForm({ ...EMPTY, registeredTeamId: String(t.id), category, paidDate: todayIso() });
                      setShowForm(true);
                    }}
                    title="Record this team's payment"
                  >
                    <Plus size={11}/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {showForm && createPortal((
        <div className="modal" onClick={close}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={close} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Edit Payment" : "Record Payment"}>
              <form className="form" onSubmit={submit}>
                <div className="two">
                  <div>
                    <label>Team *</label>
                    <select value={form.registeredTeamId}
                      onChange={e => setForm({ ...form, registeredTeamId: e.target.value })}
                      autoFocus>
                      <option value="">Select a team…</option>
                      {teams
                        .filter(t => t.status !== "Rejected")
                        .sort((a, b) => (a.teamName || "").localeCompare(b.teamName || ""))
                        .map(t => (
                          <option key={t.id} value={t.id}>{t.teamName}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label>Category *</label>
                    <select value={form.category}
                      onChange={e => setForm({ ...form, category: e.target.value })}>
                      {TEAM_FEE_CATEGORIES.map(c => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="two">
                  <div>
                    <label>Amount *</label>
                    <input type="number" step="0.01" required
                      value={form.amount}
                      onChange={e => setForm({ ...form, amount: e.target.value })}
                      placeholder="4950.00"/>
                  </div>
                  <div>
                    <label>Paid Date *</label>
                    <input type="date" required
                      value={form.paidDate}
                      onChange={e => setForm({ ...form, paidDate: e.target.value })}/>
                  </div>
                </div>

                <label>Payment Mode</label>
                <input list="fin-payment-modes"
                  value={form.paymentMode}
                  onChange={e => setForm({ ...form, paymentMode: e.target.value })}
                  placeholder="Zelle / Check / Cash…"/>
                <datalist id="fin-payment-modes">
                  {PAYMENT_MODES.map(m => <option key={m} value={m}/>)}
                </datalist>

                <label>Notes</label>
                <textarea rows={2} value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Check number, sender details, etc."/>

                <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:6}}>
                  <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Record Payment"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
